import { useRef, useMemo, useEffect } from 'react';
import { isInternalBool } from '../../utils/library-helper';
import {
  BaseFilterState,
  ResourceFacets,
  useResourceFilters,
  useResourceFilterOptions,
} from './use-resource-filters';

export interface LibraryFilterState extends BaseFilterState {
  locations: string[],
}

const initialLibraryFilters: LibraryFilterState = {
  mediaTypes: [],
  skillCategoryIds: [],
  locations: [],
  searchQuery: '',
};

export function useLibraryFilters(facets?: ResourceFacets) {
  const filterHook = useResourceFilters({
    initialFilters: initialLibraryFilters,
    debounceDelay: 300,
  });

  const filterOptions = useResourceFilterOptions(facets);
  const previousSearchQuery = useRef(filterHook.debouncedFilters.searchQuery);

  // Reset page when debounced search query actually changes
  useEffect(() => {
    if (previousSearchQuery.current !== filterHook.debouncedFilters.searchQuery) {
      filterHook.setIsReset(false);
      filterHook.setCurrentPage(0);
      previousSearchQuery.current = filterHook.debouncedFilters.searchQuery;
    }
  }, [filterHook, filterHook.debouncedFilters.searchQuery]);

  const resourceParams = useMemo(() => {
    const pageSize = 20; // Library uses 20 items per page

    return {
      mediaTypes: filterHook.filters.mediaTypes.join(','),
      skillCategories: filterHook.filters.skillCategoryIds
        .map(
          (id: string) =>
            filterOptions.uniqueSkillCategoryOptions?.find((sc: any) => sc.value === id)?.label
        )
        .join(','),
      searchQuery: filterHook.debouncedFilters.searchQuery,
      isInternal:
        filterHook.filters.locations?.length === 1
          ? isInternalBool(filterHook.filters.locations[0])
          : undefined,
      pageNumber: filterHook.currentPage,
      pageSize: pageSize,
    };
  }, [
    filterHook.filters,
    filterHook.debouncedFilters,
    filterHook.currentPage,
    filterOptions.uniqueSkillCategoryOptions,
  ]);

  return {
    ...filterHook,
    ...filterOptions,
    resourceParams,
  };
}
